/**
 * A YARA rule engine that runs in the browser.
 *
 * There is no `yara` binary here and no WASM build of one, so this implements
 * the subset of the language that can be honoured honestly against a static
 * scan. The governing rule of this module is that it never overclaims: a rule
 * using a construct this engine cannot evaluate is refused, with the construct
 * named and the rule reported as unsupported, rather than silently matching
 * nothing or everything. An analyst who believes a rule covered a sample must
 * never be wrong about that.
 *
 * Supported: text, hex and regex strings; the nocase, wide, ascii, fullword
 * and private modifiers; hex wildcards, jumps and alternation; conditions over
 * string presence, occurrence counts, `N of them` / `any of` / `all of`,
 * `filesize` comparisons, and the `at` / `in` offset operators.
 *
 * Everything else is rejected by name — see UNSUPPORTED_CONSTRUCTS.
 */

import type {
  Severity,
  YaraMatch,
  YaraRuleSummary,
  YaraStringMatch,
  YaraUnsupported,
} from "./types";

/** Shown in the UI next to the rule loader, not only in the docs. */
export const YARA_SUPPORTED_SUMMARY = [
  "Strings: text, hex, and regex",
  "Modifiers: nocase, wide, ascii, fullword, private",
  "Hex: wildcards (?), jumps ([n], [n-m], [-]), alternation ((AA|BB))",
  "Conditions: and / or / not, $string, #count, any|all|N of, filesize, at, in",
].join(" · ");

export const YARA_UNSUPPORTED_CONSTRUCTS = [
  "xor / base64 / base64wide string modifiers",
  "hash string modifier",
  "for expressions",
  "function calls such as uint32() or int16be()",
  "the entrypoint variable",
  "global rules",
  "import and include statements",
  "regex backreferences, lookaround, named groups and possessive quantifiers",
];

/** A match surface. One extracted value, or the analyzed file itself. */
export interface YaraTarget {
  source: string;
  value: string;
  bytes: Uint8Array;
  /** Which of the analyzer's surfaces this target came from. */
  surface?: string;
}

const encoder = new TextEncoder();

// Bound on hex-jump expansion. A jump of [-] against an empty needle can
// otherwise walk the whole file for every starting offset.
const MAX_JUMP = 8192;
// Findings are capped like every other detection so a rule set cannot flood
// the report.
const MAX_OFFSETS_PER_STRING = 64;

type StringKind = "text" | "hex" | "regex";

interface StringFlags {
  nocase: boolean;
  wide: boolean;
  ascii: boolean;
  fullword: boolean;
  private: boolean;
}

type HexToken =
  | { kind: "byte"; value: number }
  | { kind: "wildcard" }
  | { kind: "jump"; min: number; max: number }
  | { kind: "alternation"; options: HexToken[][] };

interface StringDefinition {
  identifier: string;
  kind: StringKind;
  text: string;
  hex: HexToken[];
  regex: RegExp | null;
  flags: StringFlags;
}

type CompareOperator = "<" | ">" | "<=" | ">=" | "==" | "!=";

type Condition =
  | { t: "or"; left: Condition; right: Condition }
  | { t: "and"; left: Condition; right: Condition }
  | { t: "not"; value: Condition }
  | { t: "const"; value: boolean }
  | { t: "exists"; id: string }
  | { t: "count"; id: string; operator: CompareOperator | null; value: number }
  | { t: "filesize"; operator: CompareOperator; value: number }
  | {
      t: "of";
      quantifier: "any" | "all" | number;
      selectors: { prefix: string; wildcard: boolean }[];
      // True for `them`, which ranges over every string the rule defines rather
      // than over a written-out set.
      allStrings: boolean;
    }
  | { t: "at"; id: string; offset: number }
  | { t: "in"; id: string; from: number; to: number };

interface ParsedRule {
  name: string;
  namespace: string;
  tags: string[];
  meta: Record<string, string>;
  strings: StringDefinition[];
  condition: Condition;
}

export interface YaraParseResult {
  rules: ParsedRule[];
  unsupported: YaraUnsupported[];
  summaries: YaraRuleSummary[];
}

/** Thrown to abandon one rule; caught by the rule loop. */
class RuleUnsupported extends Error {
  constructor(
    readonly construct: string,
    detail: string,
    readonly line: number,
  ) {
    super(detail);
  }
}

function ruleIdFor(name: string) {
  // The JARLAB-* namespace belongs to the built-in detections. YARA rules get
  // their own prefix so a rule can never collide with, or inflate, a built-in.
  // Underscores are folded to dashes too: YARA names use them heavily and the
  // built-in scheme is dash-separated.
  const suffix = name.toUpperCase().replace(/[^A-Z0-9]+/g, "-");
  return `YARA-${suffix || "UNNAMED"}`;
}

function isWordByte(byte: number) {
  return (
    (byte >= 0x30 && byte <= 0x39) ||
    (byte >= 0x41 && byte <= 0x5a) ||
    (byte >= 0x61 && byte <= 0x7a) ||
    byte === 0x5f
  );
}

function foldByte(byte: number) {
  return byte >= 0x41 && byte <= 0x5a ? byte + 0x20 : byte;
}

function textToBytes(text: string, wide: boolean) {
  if (!wide) return encoder.encode(text);
  const bytes = new Uint8Array(text.length * 2);
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    bytes[index * 2] = code & 0xff;
    bytes[index * 2 + 1] = (code >> 8) & 0xff;
  }
  return bytes;
}

/** Every offset at which `needle` occurs, optionally case-folded. */
function findBytes(
  haystack: Uint8Array,
  needle: Uint8Array,
  nocase: boolean,
  limit: number,
) {
  const found: number[] = [];
  if (!needle.length || haystack.length < needle.length) return found;
  const last = haystack.length - needle.length;
  for (let start = 0; start <= last; start += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      const a = haystack[start + offset];
      const b = needle[offset];
      if (nocase ? foldByte(a) !== foldByte(b) : a !== b) {
        matched = false;
        break;
      }
    }
    if (matched) {
      found.push(start);
      if (found.length >= limit) break;
    }
  }
  return found;
}

function matchHexTokens(
  tokens: HexToken[],
  bytes: Uint8Array,
  start: number,
): number {
  if (!tokens.length) return start;
  const [token, ...rest] = tokens;
  switch (token.kind) {
    case "byte":
      return start < bytes.length && bytes[start] === token.value
        ? matchHexTokens(rest, bytes, start + 1)
        : -1;
    case "wildcard":
      return start < bytes.length
        ? matchHexTokens(rest, bytes, start + 1)
        : -1;
    case "jump": {
      const limit = Math.min(
        token.max === Number.POSITIVE_INFINITY
          ? start + MAX_JUMP
          : start + token.max,
        bytes.length,
      );
      for (let skip = token.min; skip <= limit; skip += 1) {
        const end = matchHexTokens(rest, bytes, start + skip);
        if (end !== -1) return end;
      }
      return -1;
    }
    case "alternation":
      for (const option of token.options) {
        const end = matchHexTokens([...option, ...rest], bytes, start);
        if (end !== -1) return end;
      }
      return -1;
  }
}

function findHex(tokens: HexToken[], bytes: Uint8Array, limit: number) {
  const found: number[] = [];
  for (let start = 0; start < bytes.length; start += 1) {
    if (matchHexTokens(tokens, bytes, start) !== -1) {
      found.push(start);
      if (found.length >= limit) break;
    }
  }
  return found;
}

/** Byte-faithful text view: one char per byte, so offsets are byte offsets. */
function latin1(bytes: Uint8Array) {
  let text = "";
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    text += String.fromCharCode(
      ...bytes.subarray(index, Math.min(index + chunk, bytes.length)),
    );
  }
  return text;
}

function renderPreview(bytes: Uint8Array, start: number, length: number) {
  const slice = bytes.subarray(start, start + Math.max(0, length));
  return Array.from(slice, (byte) =>
    byte >= 0x20 && byte < 0x7f ? String.fromCharCode(byte) : ".",
  ).join("");
}

function compareNumbers(
  left: number,
  operator: CompareOperator,
  right: number,
) {
  switch (operator) {
    case "<":
      return left < right;
    case ">":
      return left > right;
    case "<=":
      return left <= right;
    case ">=":
      return left >= right;
    case "==":
      return left === right;
    case "!=":
      return left !== right;
  }
}

class RuleParser {
  /** Exposed so the rule loop can report position and skip a bad rule. */
  index = 0;

  constructor(
    private readonly text: string,
    private readonly lineStarts: number[],
  ) {}

  private lineAt(position: number) {
    let low = 0;
    let high = this.lineStarts.length - 1;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (this.lineStarts[mid] <= position) low = mid;
      else high = mid - 1;
    }
    return low + 1;
  }

  /** Always throws; typed `never` so the compiler knows control stops here. */
  private fail(construct: string, detail: string, at = this.index): never {
    throw new RuleUnsupported(construct, detail, this.lineAt(at));
  }

  private skip() {
    for (;;) {
      while (this.index < this.text.length && /\s/.test(this.text[this.index])) {
        this.index += 1;
      }
      if (this.text.startsWith("//", this.index)) {
        const end = this.text.indexOf("\n", this.index);
        this.index = end === -1 ? this.text.length : end;
        continue;
      }
      if (this.text.startsWith("/*", this.index)) {
        const end = this.text.indexOf("*/", this.index + 2);
        this.index = end === -1 ? this.text.length : end + 2;
        continue;
      }
      return;
    }
  }

  private peek(text: string) {
    this.skip();
    return this.text.startsWith(text, this.index);
  }

  private take(text: string) {
    if (!this.peek(text)) return false;
    this.index += text.length;
    return true;
  }

  private takeWord(word: string) {
    if (!this.peek(word)) return false;
    const after = this.text[this.index + word.length];
    if (after && /[\w]/.test(after)) return false;
    this.index += word.length;
    return true;
  }

  private identifier() {
    this.skip();
    const match = /^[A-Za-z_][\w]*/.exec(this.text.slice(this.index));
    if (!match) this.fail("identifier", "Expected an identifier.");
    this.index += match[0].length;
    return match[0];
  }

  private readQuoted() {
    this.skip();
    const quote = this.text[this.index];
    if (quote !== '"' && quote !== "'") {
      this.fail("string literal", "Expected a quoted string.");
    }
    const start = this.index;
    this.index += 1;
    let value = "";
    while (this.index < this.text.length) {
      const char = this.text[this.index];
      if (char === "\\") {
        const escaped = this.text[this.index + 1];
        if (escaped === undefined) break;
        value +=
          escaped === "n"
            ? "\n"
            : escaped === "t"
              ? "\t"
              : escaped === "r"
                ? "\r"
                : escaped === "x"
                  ? String.fromCharCode(
                      parseInt(this.text.slice(this.index + 2, this.index + 4), 16),
                    )
                  : escaped;
        this.index += escaped === "x" ? 4 : 2;
        continue;
      }
      if (char === quote) {
        this.index += 1;
        return value;
      }
      value += char;
      this.index += 1;
    }
    this.fail("string literal", "Unterminated string literal.", start);
    return "";
  }

  private readRegex() {
    const start = this.index;
    this.index += 1; // opening slash
    let pattern = "";
    let inClass = false;
    while (this.index < this.text.length) {
      const char = this.text[this.index];
      if (char === "\\") {
        pattern += char + (this.text[this.index + 1] ?? "");
        this.index += 2;
        continue;
      }
      if (char === "[") inClass = true;
      else if (char === "]") inClass = false;
      else if (char === "/" && !inClass) {
        this.index += 1;
        const flags = /^[a-z]*/.exec(this.text.slice(this.index))?.[0] ?? "";
        this.index += flags.length;
        return { pattern, flags };
      }
      pattern += char;
      this.index += 1;
    }
    this.fail("regular expression", "Unterminated regular expression.", start);
    return { pattern: "", flags: "" };
  }

  private readHexBlock() {
    const start = this.index;
    this.index += 1; // opening brace
    const tokens: HexToken[] = [];
    const pending: HexToken[] = [];
    let alternation: HexToken[][] | null = null;

    const push = (token: HexToken) => {
      if (alternation) alternation[alternation.length - 1].push(token);
      else pending.push(token);
    };

    for (;;) {
      this.skip();
      if (this.index >= this.text.length) {
        this.fail("hex string", "Unterminated hex string.", start);
      }
      if (this.text[this.index] === "}") {
        this.index += 1;
        break;
      }
      if (this.take("|")) {
        if (!alternation) alternation = [pending, []];
        else alternation.push([]);
        continue;
      }
      if (this.take("(")) {
        if (!alternation) alternation = [[]];
        continue;
      }
      if (this.take(")")) continue;
      if (this.take("?")) {
        push({ kind: "wildcard" });
        continue;
      }
      if (this.take("[")) {
        const jumpStart = this.index;
        const inner = this.text.slice(this.index);
        const unbounded = /^\s*-/.test(inner);
        const bounded = /^\s*(\d*)\s*(-\s*(\d*))?\s*\]/.exec(inner);
        if (unbounded) {
          const close = inner.indexOf("]");
          if (close === -1) this.fail("hex jump", "Unterminated hex jump.", jumpStart);
          this.index += close + 1;
          push({ kind: "jump", min: 0, max: Number.POSITIVE_INFINITY });
        } else if (bounded) {
          this.index += bounded[0].length;
          const min = bounded[1] ? Number(bounded[1]) : 0;
          const max = bounded[3] ? Number(bounded[3]) : min;
          if (max < min) this.fail("hex jump", "Hex jump range is inverted.", jumpStart);
          push({ kind: "jump", min, max });
        } else {
          this.fail("hex jump", "Unparsable hex jump.", jumpStart);
        }
        continue;
      }
      const hex = /^[0-9a-fA-F]{2}/.exec(this.text.slice(this.index));
      if (!hex) {
        this.fail(
          "hex string",
          "Expected a hex byte, wildcard, jump or alternation.",
          this.index,
        );
      }
      push({ kind: "byte", value: parseInt(hex[0], 16) });
      this.index += 2;
    }

    if (alternation) {
      if (alternation.length < 2) {
        this.fail("hex alternation", "Alternation needs at least two branches.", start);
      }
      tokens.push({ kind: "alternation", options: alternation });
    } else {
      tokens.push(...pending);
    }
    if (!tokens.length) this.fail("hex string", "Hex string is empty.", start);
    return tokens;
  }

  private number() {
    this.skip();
    const start = this.index;
    const hex = /^0[xX][0-9a-fA-F]+/.exec(this.text.slice(this.index));
    if (hex) {
      this.index += hex[0].length;
      return { value: parseInt(hex[0], 16), start };
    }
    const dec = /^\d+(\.\d+)?/.exec(this.text.slice(this.index));
    if (!dec) this.fail("number", "Expected a number.", start);
    this.index += dec[0].length;
    let value = Number(dec[0]);
    const unit = /^\s*(KB|MB)/i.exec(this.text.slice(this.index));
    if (unit) {
      this.index += unit[0].length;
      value *= unit[1].toUpperCase() === "KB" ? 1024 : 1024 * 1024;
    }
    return { value, start };
  }

  // --- conditions -------------------------------------------------------

  private comparison(): CompareOperator | null {
    for (const operator of ["<=", ">=", "==", "!=", "<", ">"] as const) {
      if (this.take(operator)) return operator;
    }
    return null;
  }

  parseCondition(): Condition {
    const condition = this.orCondition();
    this.skip();
    // The condition ends at the rule body's closing brace, not at end of file.
    if (this.text[this.index] !== "}") {
      this.fail(
        "condition",
        `Unexpected trailing input in the condition: ${this.text
          .slice(this.index, this.index + 24)
          .trim()}`,
      );
    }
    return condition;
  }

  private orCondition(): Condition {
    let left = this.andCondition();
    for (;;) {
      this.skip();
      if (!this.takeWord("or")) return left;
      left = { t: "or", left, right: this.andCondition() };
    }
  }

  private andCondition(): Condition {
    let left = this.notCondition();
    for (;;) {
      this.skip();
      if (!this.takeWord("and")) return left;
      left = { t: "and", left, right: this.notCondition() };
    }
  }

  private notCondition(): Condition {
    this.skip();
    if (this.takeWord("not")) return { t: "not", value: this.notCondition() };
    return this.primary();
  }

  private primary(): Condition {
    this.skip();
    if (this.take("(")) {
      const inner = this.orCondition();
      if (!this.take(")")) this.fail("condition", "Unclosed parenthesis.");
      return inner;
    }
    if (this.takeWord("true")) return { t: "const", value: true };
    if (this.takeWord("false")) return { t: "const", value: false };

    if (this.takeWord("for")) {
      this.fail("for expression", "for expressions are not supported.");
    }
    if (this.takeWord("entrypoint")) {
      this.fail("entrypoint", "The entrypoint variable is not supported.");
    }
    if (/^(?:u?int|double)\d*(?:be)?\s*\(/.test(this.text.slice(this.index))) {
      this.fail(
        "function call",
        "Numeric functions such as uint32() are not supported.",
      );
    }

    if (this.peek("filesize")) {
      this.takeWord("filesize");
      const operator = this.comparison();
      if (!operator) this.fail("filesize", "filesize needs a comparison operator.");
      const { value } = this.number();
      return { t: "filesize", operator, value };
    }

    if (this.peek("#")) {
      this.index += 1;
      const id = `$${this.identifier()}`;
      this.skip();
      if (this.takeWord("in") || this.takeWord("at")) {
        this.fail("condition", "Counts cannot be used with at/in.");
      }
      const operator = this.comparison();
      if (!operator) return { t: "count", id, operator: null, value: 0 };
      const { value } = this.number();
      return { t: "count", id, operator, value };
    }

    if (this.peek("$")) {
      this.index += 1;
      const id = `$${this.identifier()}`;
      this.skip();
      if (this.takeWord("at")) {
        const { value } = this.number();
        return { t: "at", id, offset: value };
      }
      if (this.takeWord("in")) {
        if (!this.take("(")) this.fail("condition", "in needs a (a..b) range.");
        const from = this.number().value;
        if (!this.take("..")) this.fail("condition", "in needs a (a..b) range.");
        const to = this.number().value;
        if (!this.take(")")) this.fail("condition", "Unclosed range in.");
        return { t: "in", id, from, to };
      }
      this.skip();
      if (this.takeWord("matches") || this.takeWord("contains") || this.takeWord("icontains")) {
        this.fail(
          "string operator",
          "matches/contains on strings are not supported.",
        );
      }
      return { t: "exists", id };
    }

    // quantifiers: any / all / N of ...
    this.skip();
    if (this.takeWord("any")) return this.ofCondition("any");
    if (this.takeWord("all")) return this.ofCondition("all");
    this.skip();
    if (/^\d/.test(this.text[this.index] ?? "")) {
      const leading = this.number();
      this.skip();
      if (this.text.startsWith("of", this.index)) {
        const after = this.text[this.index + 2];
        if (!after || !/[\w]/.test(after)) {
          return this.ofCondition(leading.value);
        }
      }
      this.index = leading.start;
    }
    this.skip();
    if (this.takeWord("them")) {
      this.fail(
        "bare 'them'",
        "'them' must be used as 'any|all|N of them'.",
      );
    }
    this.fail(
      "condition",
      `Unrecognized condition: ${this.text.slice(this.index, this.index + 28).trim()}`,
    );
  }

  private ofCondition(quantifier: "any" | "all" | number): Condition {
    this.skip();
    if (!this.takeWord("of")) this.fail("condition", "Expected 'of'.");
    this.skip();
    const selectors: { prefix: string; wildcard: boolean }[] = [];
    // "them" means every string the rule defines, which an empty-prefix
    // wildcard already expresses.
    if (this.takeWord("them")) {
      return {
        t: "of",
        quantifier,
        selectors: [{ prefix: "", wildcard: true }],
        allStrings: true,
      };
    }
    if (this.take("(")) {
      for (;;) {
        this.skip();
        if (!this.take("$")) this.fail("condition", "Expected a $string in the set.");
        this.skip();
        // A bare "$*" selects every string; "$pre*" selects by prefix.
        if (this.take("*")) {
          selectors.push({ prefix: "", wildcard: true });
        } else {
          const name = this.identifier();
          selectors.push({ prefix: name, wildcard: this.take("*") });
        }
        this.skip();
        if (this.take(",")) continue;
        if (this.take(")")) break;
        this.fail("condition", "Expected ',' or ')' in the string set.");
      }
      return { t: "of", quantifier, selectors, allStrings: false };
    }
    this.fail("condition", "Expected 'them' or a ($string*) set after 'of'.");
  }

  // --- strings ----------------------------------------------------------

  parseStringModifiers(stringKind: StringKind): StringFlags {
    const flags: StringFlags = {
      nocase: false,
      wide: false,
      ascii: false,
      fullword: false,
      private: false,
    };
    for (;;) {
      this.skip();
      if (this.takeWord("nocase")) {
        if (stringKind === "hex") {
          this.fail("nocase on a hex string", "nocase has no meaning on a hex string.");
        }
        flags.nocase = true;
        continue;
      }
      if (this.takeWord("fullword")) {
        if (stringKind === "hex") {
          this.fail(
            "fullword on a hex string",
            "fullword has no meaning on a hex string.",
          );
        }
        flags.fullword = true;
        continue;
      }
      if (this.takeWord("wide")) {
        flags.wide = true;
        continue;
      }
      if (this.takeWord("ascii")) {
        flags.ascii = true;
        continue;
      }
      if (this.takeWord("private")) {
        flags.private = true;
        continue;
      }
      for (const modifier of ["xor", "base64", "base64wide", "hash"]) {
        if (this.peek(modifier)) {
          const after = this.text[this.index + modifier.length];
          if (!after || !/[\w]/.test(after)) {
            this.fail(
              `${modifier} modifier`,
              `The ${modifier} string modifier is not supported.`,
            );
          }
        }
      }
      return flags;
    }
  }

  private readStringDefinition(): StringDefinition {
    this.skip();
    if (!this.take("$")) this.fail("string identifier", "Expected a $string name.");
    const identifier = `$${this.identifier()}`;
    if (!this.take("=")) this.fail("string definition", "Expected '=' after the $name.");
    this.skip();

    let kind: StringKind = "text";
    let text = "";
    let hex: HexToken[] = [];
    let regex: RegExp | null = null;

    if (this.peek("{")) {
      kind = "hex";
      hex = this.readHexBlock();
    } else if (this.peek("/")) {
      kind = "regex";
      const { pattern, flags } = this.readRegex();
      regex = this.compileRegex(pattern, flags);
      text = pattern;
    } else {
      kind = "text";
      text = this.readQuoted();
    }

    const flags = this.parseStringModifiers(kind);
    if (kind === "text" && !flags.ascii && !flags.wide) flags.ascii = true;
    if (kind === "text" && !/[ -~]/.test(text) && !flags.wide) {
      this.fail(
        "non-ASCII text string",
        "A non-ASCII text string needs the wide modifier.",
      );
    }
    return { identifier, kind, text, hex, regex, flags };
  }

  private compileRegex(pattern: string, flags: string): RegExp {
    const unsupported: [RegExp, string, string][] = [
      [/\(\?=/, "lookaround", "Lookahead"],
      [/\(\?!/, "lookaround", "Negative lookahead"],
      [/\(\?<=/, "lookbehind", "Lookbehind"],
      [/\(\?<!/, "lookbehind", "Negative lookbehind"],
      [/\\[1-9]\d?/, "backreference", "Backreferences"],
      [/\(\?<[A-Za-z_]/, "named group", "Named capture groups"],
      [/[+*?]\+/, "possessive quantifier", "Possessive quantifiers"],
      [/\\[gZ]/, "subroutine call", "Subroutine calls"],
    ];
    for (const [test, construct, label] of unsupported) {
      if (test.test(pattern)) {
        this.fail(construct, `${label} in a regex is not supported.`);
      }
    }
    // A bare \ followed by a digit is a backreference; the group above catches
    // the common cases, this catches octal escapes we cannot reason about.
    try {
      return new RegExp(pattern, `${flags}g`);
    } catch (error) {
      this.fail("regular expression", (error as Error).message);
    }
  }

  // --- rule -------------------------------------------------------------

  parseRule(namespace: string): ParsedRule {
    this.skip();
    const line = this.lineAt(this.index);
    if (!this.takeWord("rule")) {
      this.fail(
        "rule declaration",
        "Expected 'rule' at the start of a declaration.",
      );
    }
    const name = this.identifier();
    const tags: string[] = [];
    this.skip();
    if (this.take(":")) {
      for (;;) {
        this.skip();
        if (/^rule\b/.test(this.text.slice(this.index))) break;
        if (!/^[\w*]/.test(this.text[this.index] ?? "")) break;
        tags.push(this.identifier());
      }
    }
    this.skip();
    if (this.takeWord("global")) {
      this.fail("global rule", "Global rules are not supported.");
    }
    if (this.takeWord("private")) {
      // Private only changes reporting, not whether the condition holds.
    }
    if (!this.take("{")) this.fail("rule declaration", "Expected '{' after the rule name.", line);

    const meta: Record<string, string> = {};
    const strings: StringDefinition[] = [];
    let condition: Condition | null = null;

    for (;;) {
      this.skip();
      if (this.take("}")) break;
      if (this.index >= this.text.length) {
        this.fail("rule declaration", "Unterminated rule body.", line);
      }
      if (this.takeWord("meta")) {
        if (!this.take(":")) this.fail("meta section", "Expected ':' after meta.");
        for (;;) {
          this.skip();
          if (/^(?:strings|condition)\b/.test(this.text.slice(this.index))) break;
          if (this.peek("}")) break;
          const key = this.identifier();
          if (!this.take("=")) this.fail("meta section", "Expected '=' in meta.");
          this.skip();
          if (this.peek('"')) meta[key] = this.readQuoted();
          else if (this.peek("-") || /\d/.test(this.text[this.index] ?? "")) {
            meta[key] = String(this.number().value);
          } else if (this.takeWord("true")) meta[key] = "true";
          else if (this.takeWord("false")) meta[key] = "false";
          else this.fail("meta section", `Unsupported meta value for '${key}'.`);
        }
        continue;
      }
      if (this.takeWord("strings")) {
        if (!this.take(":")) this.fail("strings section", "Expected ':' after strings.");
        for (;;) {
          this.skip();
          if (this.peek("}")) break;
          if (/^condition\b/.test(this.text.slice(this.index))) break;
          if (!this.peek("$")) break;
          strings.push(this.readStringDefinition());
        }
        continue;
      }
      if (this.takeWord("condition")) {
        if (!this.take(":")) this.fail("condition", "Expected ':' after condition.");
        condition = this.parseCondition();
        continue;
      }
      this.fail(
        "rule section",
        `Expected meta, strings or condition; found "${this.text
          .slice(this.index, this.index + 20)
          .trim()}"`,
      );
    }

    if (!condition) {
      this.fail("condition", "Rule has no condition section.", line);
    }
    return { name, namespace, tags, meta, strings, condition };
  }

  /** Skip to the end of a rule so one bad rule does not sink the file. */
  skipRule() {
    let depth = 0;
    for (;;) {
      if (this.index >= this.text.length) return;
      const char = this.text[this.index];
      if (char === "{") depth += 1;
      if (char === "}") {
        this.index += 1;
        depth -= 1;
        if (depth <= 0) return;
        continue;
      }
      this.index += 1;
    }
  }

  atEnd() {
    this.skip();
    return this.index >= this.text.length;
  }

  atWord(word: string) {
    this.skip();
    if (!this.text.startsWith(word, this.index)) return false;
    const after = this.text[this.index + word.length];
    return !after || !/[\w]/.test(after);
  }
}

function lineStarts(text: string) {
  const starts = [0];
  for (let index = 0; index < text.length; index += 1) {
    if (text[index] === "\n") starts.push(index + 1);
  }
  return starts;
}

/** Rule names must be unique or findings would collapse together. */
function duplicateRuleNames(rules: ParsedRule[]) {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const rule of rules) {
    if (seen.has(rule.name)) duplicates.add(rule.name);
    seen.add(rule.name);
  }
  return duplicates;
}

export function parseYaraRules(namespace: string, text: string): YaraParseResult {
  const rules: ParsedRule[] = [];
  const unsupported: YaraUnsupported[] = [];
  const parser = new RuleParser(text, lineStarts(text));

  while (!parser.atEnd()) {
    if (parser.atWord("global")) {
      const nameMatch = /global\s+(?:private\s+)?rule\s+([A-Za-z_][\w]*)/.exec(
        text.slice(parser.index),
      );
      unsupported.push({
        rule: nameMatch?.[1] ?? "(unparsed)",
        construct: "global rule",
        detail:
          "Global rules are not supported: their condition applies to every scanned surface, which this multi-surface engine cannot reproduce.",
        line: 1,
      });
      const end = text.indexOf("\n", parser.index + 1);
      parser.index = end === -1 ? text.length : end;
      continue;
    }
    if (parser.atWord("import") || parser.atWord("include")) {
      const line = 1;
      unsupported.push({
        rule: "(file scope)",
        construct: "import / include",
        detail: `${parser.atWord("import") ? "import" : "include"} statements are not supported; the rule file must be self-contained.`,
        line,
      });
      // Step over the whole directive line.
      const end = text.indexOf("\n", parser.index + 1);
      parser.index = end === -1 ? text.length : end;
      continue;
    }
    if (!parser.atWord("rule")) {
      unsupported.push({
        rule: "(file scope)",
        construct: "unknown declaration",
        detail: `Expected 'rule' but found "${text.slice(parser.index, parser.index + 32).trim()}".`,
        line: 1,
      });
      while (parser.index < text.length && text[parser.index] !== "\n") {
        parser.index += 1;
      }
      continue;
    }
    const ruleLine = parser.index;
    let currentName = "(unparsed)";
    try {
      // Peek the name so a diagnostic can name the rule that failed.
      const nameMatch = /^rule\s+([A-Za-z_][\w]*)/.exec(text.slice(ruleLine));
      if (nameMatch) currentName = nameMatch[1];
      const rule = parser.parseRule(namespace);
      rules.push(rule);
    } catch (error) {
      if (!(error instanceof RuleUnsupported)) throw error;
      unsupported.push({
        rule: currentName,
        construct: error.construct,
        detail: error.message,
        line: error.line,
      });
      parser.skipRule();
    }
  }

  const duplicates = duplicateRuleNames(rules);
  if (duplicates.size) {
    unsupported.push({
      rule: [...duplicates].join(", "),
      construct: "duplicate rule name",
      detail:
        "Two rules share a name, so their findings would collapse into one. Rename them before loading.",
      line: 1,
    });
  }

  return {
    rules,
    unsupported,
    summaries: rules.map((rule) => ({
      name: rule.name,
      ruleId: ruleIdFor(rule.name),
      namespace: rule.namespace,
      tags: rule.tags,
      stringCount: rule.strings.length,
    })),
  };
}

function matchString(definition: StringDefinition, bytes: Uint8Array) {
  if (definition.kind === "hex") {
    return findHex(definition.hex, bytes, MAX_OFFSETS_PER_STRING);
  }
  if (definition.kind === "regex") {
    const wideFactor = definition.flags.wide && !definition.flags.ascii ? 2 : 1;
    const pattern = new RegExp(
      definition.regex!.source,
      `${definition.regex!.flags}${definition.flags.nocase ? "i" : ""}${
        definition.flags.wide ? "" : ""
      }`,
    );
    pattern.lastIndex = 0;
    const view = latin1(bytes);
    const found: number[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(view)) !== null) {
      found.push(match.index * wideFactor);
      if (match[0] === "") pattern.lastIndex += 1;
      if (found.length >= MAX_OFFSETS_PER_STRING) break;
    }
    return found;
  }

  const encodings: Uint8Array[] = [];
  if (definition.flags.wide) encodings.push(textToBytes(definition.text, true));
  if (definition.flags.ascii || !definition.flags.wide) {
    encodings.push(textToBytes(definition.text, false));
  }
  const found: number[] = [];
  for (const encoding of encodings) {
    for (const offset of findBytes(
      bytes,
      encoding,
      definition.flags.nocase,
      MAX_OFFSETS_PER_STRING,
    )) {
      if (definition.flags.fullword) {
        const before = offset > 0 ? bytes[offset - 1] : -1;
        const after =
          offset + encoding.length < bytes.length
            ? bytes[offset + encoding.length]
            : -1;
        if (
          (before !== -1 && isWordByte(before)) ||
          (after !== -1 && isWordByte(after))
        ) {
          continue;
        }
      }
      found.push(offset);
      if (found.length >= MAX_OFFSETS_PER_STRING) break;
    }
  }
  return [...new Set(found)].sort((left, right) => left - right);
}

type MatchSet = Map<string, { offsets: number[]; length: number }>;

function evaluate(
  condition: Condition,
  matches: MatchSet,
  definitions: Map<string, StringDefinition>,
  fileSize: number,
): boolean {
  switch (condition.t) {
    case "const":
      return condition.value;
    case "or":
      return (
        evaluate(condition.left, matches, definitions, fileSize) ||
        evaluate(condition.right, matches, definitions, fileSize)
      );
    case "and":
      return (
        evaluate(condition.left, matches, definitions, fileSize) &&
        evaluate(condition.right, matches, definitions, fileSize)
      );
    case "not":
      return !evaluate(condition.value, matches, definitions, fileSize);
    case "exists":
      return (matches.get(condition.id)?.offsets.length ?? 0) > 0;
    case "count": {
      const count = matches.get(condition.id)?.offsets.length ?? 0;
      if (!condition.operator) return count > 0;
      return compareNumbers(count, condition.operator, condition.value);
    }
    case "filesize":
      return compareNumbers(fileSize, condition.operator, condition.value);
    case "at":
      return (matches.get(condition.id)?.offsets ?? []).includes(condition.offset);
    case "in":
      return (matches.get(condition.id)?.offsets ?? []).some(
        (offset) => offset >= condition.from && offset <= condition.to,
      );
    case "of": {
      // Membership (which strings the selector names) and occurrence (which of
      // them actually hit) are different questions, and conflating them makes
      // `all of them` true whenever every string is merely defined.
      const inSet = [...matches.entries()].filter(([id]) => {
        const name = id.slice(1);
        return condition.selectors.some((selector) =>
          selector.wildcard
            ? selector.prefix === ""
              ? true
              : name.startsWith(selector.prefix)
            : name === selector.prefix,
        );
      });
      const hits = inSet.filter(([, value]) => value.offsets.length > 0).length;
      if (condition.quantifier === "any") return hits > 0;
      if (condition.quantifier === "all") {
        return inSet.length > 0 && hits === inSet.length;
      }
      return hits >= condition.quantifier;
    }
  }
}

const SEVERITIES = new Set<Severity>(["critical", "high", "medium", "low", "info"]);
const CONFIDENCES = new Set(["high", "medium", "low"]);

/**
 * Rules are analyst-authored, so `meta` is trusted to tune severity but not to
 * be required. The default is deliberately middling: a YARA hit is a real
 * signal, but an arbitrary rule is not automatically proof of malware.
 */
function severityFor(rule: ParsedRule) {
  const declared = rule.meta.severity?.toLowerCase();
  if (declared && SEVERITIES.has(declared as Severity)) return declared as Severity;
  return "high";
}

function confidenceFor(rule: ParsedRule, trusted: boolean) {
  const declared = rule.meta.confidence?.toLowerCase();
  if (declared && CONFIDENCES.has(declared)) {
    return declared as "high" | "medium" | "low";
  }
  return trusted ? "high" : "medium";
}

export interface YaraScanOptions {
  trusted: boolean;
}

export function scanYara(
  rules: ParsedRule[],
  targets: YaraTarget[],
  options: YaraScanOptions,
): YaraMatch[] {
  const matches: YaraMatch[] = [];
  for (const rule of rules) {
    const definitions = new Map(rule.strings.map((item) => [item.identifier, item]));
    let best: { target: YaraTarget; strings: YaraStringMatch[] } | null = null;

    // Each target is evaluated as if it were its own file, which is the
    // faithful reading of a rule over a multi-surface scan: a rule matches if
    // its condition holds for any single surface.
    for (const target of targets) {
      const set: MatchSet = new Map();
      for (const definition of rule.strings) {
        const offsets = matchString(definition, target.bytes);
        set.set(definition.identifier, {
          offsets,
          length:
            definition.kind === "text"
              ? textToBytes(definition.text, definition.flags.wide).length
              : definition.kind === "hex"
                ? 0
                : 8,
        });
      }
      if (!evaluate(rule.condition, set, definitions, target.bytes.length)) continue;
      const strings: YaraStringMatch[] = [];
      for (const definition of rule.strings) {
        const entry = set.get(definition.identifier);
        if (!entry?.offsets.length) continue;
        for (const offset of entry.offsets.slice(0, 4)) {
          strings.push({
            identifier: definition.identifier,
            offset,
            preview: renderPreview(
              target.bytes,
              offset,
              entry.length || 24,
            ),
          });
        }
      }
      // Prefer the surface that matched most strings: it is the most useful
      // one for an analyst to open next.
      if (!best || strings.length > best.strings.length) best = { target, strings };
    }

    if (!best) continue;
    const evidence = best.strings.map(
      (item) =>
        `${item.identifier} at offset ${item.offset}${
          item.preview ? ` · "${item.preview}"` : ""
        }`,
    );
    if (rule.meta.author) evidence.push(`Author: ${rule.meta.author}`);
    if (rule.meta.reference) evidence.push(`Reference: ${rule.meta.reference}`);
    evidence.push(`Matched surface: ${best.target.value}`);

    matches.push({
      rule: rule.name,
      ruleId: ruleIdFor(rule.name),
      namespace: rule.namespace,
      tags: rule.tags,
      meta: rule.meta,
      severity: severityFor(rule),
      confidence: confidenceFor(rule, options.trusted),
      target: best.target.source,
      surface: best.target.surface ?? "unknown surface",
      strings: best.strings,
      evidence,
    });
  }
  return matches;
}

// --- installed rule state ---------------------------------------------------

export interface YaraState {
  rules: ParsedRule[];
  unsupported: YaraUnsupported[];
  summaries: YaraRuleSummary[];
  trusted: boolean;
  namespace: string;
  /**
   * Bumped on every install and every clear. A report records the generation it
   * was scanned under, so a rule set loaded afterwards can never be reported as
   * having evaluated that sample when it never did.
   */
  generation: number;
}

const EMPTY: Omit<YaraState, "generation"> = {
  rules: [],
  unsupported: [],
  summaries: [],
  trusted: false,
  namespace: "local",
};

/**
 * The installed rule set is one thing, so it is one value.
 *
 * These were six module-level bindings that only ever changed together, which
 * meant any future field had to be remembered in three places -- the install
 * path, the clear path and the read path -- and forgetting one left a stale
 * value behind a cleared rule set. Replacing the whole object is also what
 * makes the generation counter meaningful: a reader holding a snapshot can
 * never observe a half-updated set.
 */
let state: YaraState = { ...EMPTY, generation: 0 };

export function installYaraRules(parsed: YaraParseResult, trusted: boolean, namespace: string) {
  state = {
    rules: parsed.rules,
    unsupported: parsed.unsupported,
    summaries: parsed.summaries,
    trusted,
    namespace,
    generation: state.generation + 1,
  };
}

export function clearYaraRules() {
  state = { ...EMPTY, generation: state.generation + 1 };
}

export function getYaraState(): YaraState {
  return state;
}

export { ruleIdFor };
